// ======================================================================
// FILE: src/modules/wms/dashboard/components/DashboardFilterBar.jsx
// MODULE: Work Management System (WMS)
// FEATURE: Dashboard
//
// PURPOSE:
//   Dynamic Dashboard Filter Bar
//
// RESPONSIBILITIES:
//   - Date range selection
//   - Team selection
//   - Executive selection
//   - Priority selection
//   - Status selection
//   - Work mode selection
//   - Work search
//   - Trigger dashboard search
//
// DATA:
//   Filters and master data are supplied by WMSDashboardPage.
//
// ======================================================================

import React from "react";


// ======================================================================
// DATE HELPERS
// ======================================================================

function formatDate(date) {

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(2, "0");

  const day =
    String(
      date.getDate()
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}


function getDateRange(range) {

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  const to =
    new Date(today);

  const from =
    new Date(today);


  // ----------------------------------------------------------
  // Today
  // ----------------------------------------------------------

  if (range === "TODAY") {

    return {
      date_from: formatDate(from),
      date_to: formatDate(to),
    };

  }


  // ----------------------------------------------------------
  // Last 7 Days
  // ----------------------------------------------------------

  if (range === "LAST_7_DAYS") {

    from.setDate(
      from.getDate() - 6
    );

    return {
      date_from: formatDate(from),
      date_to: formatDate(to),
    };

  }


  // ----------------------------------------------------------
  // Last 30 Days
  // ----------------------------------------------------------

  if (range === "LAST_30_DAYS") {

    from.setDate(
      from.getDate() - 29
    );

    return {
      date_from: formatDate(from),
      date_to: formatDate(to),
    };

  }


  // ----------------------------------------------------------
  // Last 90 Days
  // ----------------------------------------------------------

  if (range === "LAST_90_DAYS") {

    from.setDate(
      from.getDate() - 89
    );

    return {
      date_from: formatDate(from),
      date_to: formatDate(to),
    };

  }


  // ----------------------------------------------------------
  // This Year
  // ----------------------------------------------------------

  if (range === "THIS_YEAR") {

    from.setMonth(0);
    from.setDate(1);

    return {
      date_from: formatDate(from),
      date_to: formatDate(to),
    };

  }


  // ----------------------------------------------------------
  // All / no date filter
  // ----------------------------------------------------------

  return {
    date_from: "",
    date_to: "",
  };

}


// ======================================================================
// DETERMINE CURRENT DATE RANGE VALUE
// ======================================================================

function getSelectedDateRange(filters) {

  if (
    !filters?.date_from &&
    !filters?.date_to
  ) {
    return "";
  }

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  const todayString =
    formatDate(today);


  // ----------------------------------------------------------
  // Today
  // ----------------------------------------------------------

  if (
    filters.date_from === todayString &&
    filters.date_to === todayString
  ) {
    return "TODAY";
  }


  // ----------------------------------------------------------
  // Compare generated ranges
  // ----------------------------------------------------------

  const ranges = [
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "LAST_90_DAYS",
    "THIS_YEAR",
  ];


  for (
    const range of ranges
  ) {

    const dates =
      getDateRange(range);

    if (
      dates.date_from ===
        filters.date_from &&
      dates.date_to ===
        filters.date_to
    ) {
      return range;
    }

  }


  return "";
}


// ======================================================================
// COMPONENT
// ======================================================================

export default function DashboardFilterBar({

  filters = {},

  teams = [],

  executives = [],

  onFilterChange,

  onSearch,

  onReset,

  loading = false,

}) {


  // ====================================================================
  // HANDLE DATE RANGE
  // ====================================================================

  const handleDateRangeChange = (
    event
  ) => {

    const range =
      event.target.value;

    const dates =
      getDateRange(range);


    if (onFilterChange) {

      onFilterChange(
        "date_from",
        dates.date_from
      );

      onFilterChange(
        "date_to",
        dates.date_to
      );

    }

  };


  // ====================================================================
  // HANDLE FILTER
  // ====================================================================

  const handleChange = (
    name
  ) => (
    event
  ) => {

    if (onFilterChange) {

      onFilterChange(
        name,
        event.target.value
      );

    }

  };


  // ====================================================================
  // SELECTED DATE RANGE
  // ====================================================================

  const selectedDateRange =
    getSelectedDateRange(
      filters
    );


  // ====================================================================
  // RENDER
  // ====================================================================

  return (

    <div className="card shadow-sm mb-4">

      <div className="card-body">

        <div className="row g-3 align-items-end">


          {/* ============================================================
              DATE RANGE
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Date Range
            </label>

            <select
              className="form-select"
              value={
                selectedDateRange
              }
              onChange={
                handleDateRangeChange
              }
              disabled={loading}
            >

              <option value="">
                All Dates
              </option>

              <option value="TODAY">
                Today
              </option>

              <option value="LAST_7_DAYS">
                Last 7 Days
              </option>

              <option value="LAST_30_DAYS">
                Last 30 Days
              </option>

              <option value="LAST_90_DAYS">
                Last 90 Days
              </option>

              <option value="THIS_YEAR">
                This Year
              </option>

            </select>

          </div>


          {/* ============================================================
              TEAM
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Team
            </label>

            <select
              className="form-select"
              value={
                filters.team_id || ""
              }
              onChange={
                handleChange("team_id")
              }
              disabled={loading}
            >

              <option value="">
                All Teams
              </option>

              {teams.map(
                team => (

                  <option
                    key={team.id}
                    value={team.id}
                  >
                    {
                      team.team_name
                      ||
                      team.name
                      ||
                      team.team_code
                      ||
                      `Team ${team.id}`
                    }
                  </option>

                )
              )}

            </select>

          </div>


          {/* ============================================================
              EXECUTIVE
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Executive
            </label>

            <select
              className="form-select"
              value={
                filters.executive_id || ""
              }
              onChange={
                handleChange("executive_id")
              }
              disabled={loading}
            >

              <option value="">
                All Executives
              </option>

              {executives.map(
                member => (

                  <option
                    key={member.id}
                    value={member.id}
                  >
                    {
                      member.member_name
                      ||
                      member.name
                      ||
                      member.member_code
                      ||
                      `Executive ${member.id}`
                    }
                  </option>

                )
              )}

            </select>

          </div>


          {/* ============================================================
              PRIORITY
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Priority
            </label>

            <select
              className="form-select"
              value={
                filters.priority || ""
              }
              onChange={
                handleChange("priority")
              }
              disabled={loading}
            >

              <option value="">
                All
              </option>

              <option value="HIGH">
                High
              </option>

              <option value="MEDIUM">
                Medium
              </option>

              <option value="LOW">
                Low
              </option>

            </select>

          </div>


          {/* ============================================================
              STATUS
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Status
            </label>

            <select
              className="form-select"
              value={
                filters.status || ""
              }
              onChange={
                handleChange("status")
              }
              disabled={loading}
            >

              <option value="">
                All
              </option>

              <option value="OPEN">
                Open
              </option>

              <option value="IN_PROGRESS">
                In Progress
              </option>

              <option value="ON_HOLD">
                On Hold
              </option>

              <option value="WAITING_FOR_APPROVAL">
                Waiting for Approval
              </option>

              <option value="CLOSED">
                Closed
              </option>

              <option value="CANCELLED">
                Cancelled
              </option>

              <option value="REJECTED">
                Rejected
              </option>

            </select>

          </div>


          {/* ============================================================
              WORK MODE
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 col-md-6">

            <label className="form-label fw-semibold">
              Work Mode
            </label>

            <select
              className="form-select"
              value={
                filters.work_mode || ""
              }
              onChange={
                handleChange("work_mode")
              }
              disabled={loading}
            >

              <option value="">
                All
              </option>

              <option value="ADHOC">
                Adhoc
              </option>

              <option value="TEMPLATE">
                Template
              </option>

            </select>

          </div>


          {/* ============================================================
              SEARCH
              ============================================================ */}

          <div className="col-xl-8 col-lg-7">

            <label className="form-label fw-semibold">
              Search
            </label>

            <input
              type="text"
              className="form-control"
              placeholder="Search Work No, Title..."
              value={
                filters.search || ""
              }
              onChange={
                handleChange("search")
              }
              disabled={loading}
            />

          </div>


          {/* ============================================================
              BUTTONS
              ============================================================ */}

          <div className="col-xl-2 col-lg-3 d-flex gap-2">

            <button
              className="btn btn-primary flex-grow-1"
              type="button"
              onClick={
                onSearch
              }
              disabled={loading}
            >

              {loading ? (

                <>

                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />

                  Loading

                </>

              ) : (

                <>

                  <i className="fas fa-search me-2" />

                  Search

                </>

              )}

            </button>


            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={
                onReset
              }
              disabled={loading}
              title="Reset Filters"
            >

              <i className="fas fa-undo" />

            </button>

          </div>


        </div>

      </div>

    </div>

  );

}